import React, { useState } from 'react'
import PropTypes from 'prop-types'

import { BrandList } from 'components/BrandDirectory'
import { ChevronIcon, SearchIcon } from 'components/Icons'
import { Title42, Body12 } from 'components/Typography'
import { Container } from 'styles/sharedStyle'
import arrayUtils from 'utils/array'
import trackGTM from 'utils/trackGTM'
import { globalHistory as history } from '@reach/router'
import { useTheme } from 'styled-components'
import Fuse from 'fuse.js'
import {
  CloseButton,
  Form,
  Input,
  Label,
  ResultLabel,
  ResultString,
  SearchButton,
  SearchModal,
  Section,
} from './BrandSearch.style'

function BrandSearch({
  brands,
  villageName,
  headline,
  noResults,
  promptText,
  onClose,
  resultLabelSing,
  resultLabelPlural,
}) {
  const {
    location: { pathname },
  } = history
  const { colors, space } = useTheme()
  const groupdBrands = arrayUtils.groupArrayObjects(brands, 'ALL')
  const [animationDirection, setAnimationDirection] = useState('in')
  const [filteredBrands, setFilteredBrands] = useState(groupdBrands)
  const [hasError, setHasError] = useState(false)
  const [searchString, setSearchString] = useState('')
  const [totalResults, setTotalResults] = useState(0)
  const [displayLabel, setDisplayLabel] = useState(true)

  const getCount = (obj) =>
    Object.values(obj).reduce((acc, item) => item.length + acc, 0)

  const filterBrands = (value) => {
    if (value.length < 1) {
      setFilteredBrands(groupdBrands)
      setHasError(false)
      setTotalResults(getCount(groupdBrands))

      return false
    }

    const newBrands = Object.keys(groupdBrands).reduce((obj, key) => {
      const newObj = { ...obj }

      // Fuse is a Fuzzy Search library
      // Threshold is from 0 to 1
      // 0 = Perfect Match - 1 = No Match
      // Set it in 'threshold' in 'fuseOptions' below

      const fuseOptions = {
        includeScore: true,
        keys: ['name'],
        includeMatches: true,
        findAllMatches: true,
        threshold: 0.4, // float 0 to 1
      }

      const fuse = new Fuse(groupdBrands[key], fuseOptions)
      const foundBrandsResult = fuse.search(value)
      const foundBrands = foundBrandsResult.map((foundBrand) => foundBrand.item)

      if (foundBrands.length > 0) {
        newObj[key] = foundBrands
      }
      return newObj
    }, {})

    setTotalResults(getCount(newBrands))

    if (Object.keys(newBrands).length > 0) {
      setFilteredBrands(newBrands)
      setHasError(false)
    } else {
      setFilteredBrands(groupdBrands)
      setHasError(true)
    }

    return null
  }

  const handleClose = () => {
    setAnimationDirection('out')
    onClose()
  }

  const handleChange = (e) => {
    const { value } = e.target

    if (value.length !== 0) {
      setDisplayLabel(false)
    } else {
      setDisplayLabel(true)
    }

    setSearchString(value)
    filterBrands(value)
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    filterBrands(searchString)
    trackGTM('brands', 'brands', 'brand search', pathname)
  }

  const resultCountString =
    totalResults === 1
      ? `${totalResults} ${resultLabelSing}`
      : `${totalResults} ${resultLabelPlural}`

  return (
    <SearchModal
      animation={animationDirection}
      backgroundColor={[colors.backgroundColor, null, colors.sagelightToBG]}
      p={{ md: space[2] }}>
      <Container width="100%">
        <CloseButton onClick={handleClose} type="button">
          <ChevronIcon width={[20]} />
        </CloseButton>

        <Section display={['none', null, 'block']} textAlign="center">
          <Title42 as="h2" mt={space[7]}>
            {headline}
          </Title42>
        </Section>

        <Form onSubmit={handleSubmit}>
          <Label display={displayLabel}>
            <span>{promptText}</span>
            <Input
              as="input"
              autoComplete="off"
              id="brand-search"
              name="brand-search"
              type="search"
              leftAlign={displayLabel}
              value={searchString}
              onChange={handleChange}
              autoFocus
            />
          </Label>
          <SearchButton type="submit">
            <SearchIcon width={[15, 15, 40]} height={[15, 15, 40]} />
          </SearchButton>
        </Form>

        {searchString.length > 1 && (
          <Section as="div" px={space[2]} textAlign="center">
            <ResultString>{searchString}</ResultString>{' '}
            <ResultLabel>
              {hasError ? noResults : resultCountString}
            </ResultLabel>
          </Section>
        )}

        <Section px={space[2]} isBrandWrapper>
          {Object.keys(filteredBrands).map((key) => {
            if (filteredBrands[key].length < 1) return null

            return (
              <Section key={key}>
                <Body12 as="h3" fontWeight="normal" mt={[0]} mb={[7]}>
                  <div>{key}</div>
                </Body12>
                <BrandList
                  brands={filteredBrands[key]}
                  villageName={villageName}
                  key={key}
                  headline={headline}
                />
              </Section>
            )
          })}
        </Section>
      </Container>
    </SearchModal>
  )
}

BrandSearch.propTypes = {
  brands: PropTypes.arrayOf(
    PropTypes.shape({
      link: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
  villageName: PropTypes.string,
  headline: PropTypes.string.isRequired,
  noResults: PropTypes.string.isRequired,
  promptText: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  resultLabelSing: PropTypes.string.isRequired,
  resultLabelPlural: PropTypes.string.isRequired,
}

BrandSearch.defaultProps = {
  villageName: '',
}

export default BrandSearch
